import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, ArrowLeft, ImagePlus, X } from 'lucide-react';
import api from '../api/axios';

const DrinksManagement = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [catDialog, setCatDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', price: '', description: '', category_id: '' });
  const [catName, setCatName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchDrinks();
  }, [navigate]);

  const fetchDrinks = async () => {
    try { const res = await api.get('/menu/drinks'); setCategories(res.data); }
    catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handleAddCategory = async () => {
    if (!catName.trim()) return;
    try {
      await api.post('/menu/drinks/categories', { name: catName.trim() });
      toast({ title: 'Categorie ajoutee' });
      setCatDialog(false);
      setCatName('');
      fetchDrinks();
    } catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const handleAdd = (catId) => {
    setEditing(null);
    setForm({ name: '', price: '', description: '', category_id: catId });
    setImageFile(null);
    setImagePreview('');
    setDialog(true);
  };

  const handleEdit = (item, catId) => {
    setEditing(item);
    setForm({ name: item.name, price: item.price, description: item.description || '', category_id: catId });
    setImageFile(null);
    setImagePreview(item.image || '');
    setDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Fichier invalide', description: 'Veuillez selectionner une image', variant: 'destructive' });
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let imageUrl = editing?.image || '';

      // Upload new image if selected
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadRes = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        imageUrl = uploadRes.data.imageUrl;
      }

      const payload = { ...form, image: imageUrl };

      if (editing) {
        await api.put(`/menu/drinks/${editing.id}`, payload);
        toast({ title: 'Boisson modifiee' });
      } else {
        await api.post('/menu/drinks', payload);
        toast({ title: 'Boisson ajoutee' });
      }
      setDialog(false);
      setImageFile(null);
      setImagePreview('');
      fetchDrinks();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette boisson ?')) return;
    try { await api.delete(`/menu/drinks/${id}`); toast({ title: 'Supprimee' }); fetchDrinks(); }
    catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Gestion des Boissons</h1>
          <div className="flex gap-2">
            <Button onClick={() => setCatDialog(true)} size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs"><Plus size={14} className="mr-1" /> Categorie</Button>
            <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs"><ArrowLeft size={14} className="mr-1" /> Dashboard</Button>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {categories.map(cat => (
          <Card key={cat.id} className="border shadow-sm">
            <CardHeader className="pb-2 pt-3 px-4 flex flex-row justify-between items-center">
              <CardTitle className="text-lg">{cat.name}</CardTitle>
              <Button onClick={() => handleAdd(cat.id)} size="sm" className="bg-green-600 hover:bg-green-700 text-xs h-7"><Plus size={12} className="mr-1" /> Ajouter</Button>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="space-y-1">
                {cat.items.map(item => (
                  <div key={item.id} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded text-sm">
                    <div className="flex items-center gap-2">
                      {item.image && <img src={item.image} alt="" className="w-8 h-8 rounded object-cover" />}
                      <span className="font-medium">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#8b4513] font-bold">{item.price}&euro;</span>
                      <Button onClick={() => handleEdit(item, cat.id)} variant="ghost" size="sm" className="h-6 w-6 p-0" data-testid={`edit-drink-${item.id}`}><Edit size={12} /></Button>
                      <Button onClick={() => handleDelete(item.id)} variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500"><Trash2 size={12} /></Button>
                    </div>
                  </div>
                ))}
                {cat.items.length === 0 && <p className="text-xs text-gray-400 py-2">Aucune boisson dans cette categorie</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{editing ? 'Modifier la boisson' : 'Nouvelle boisson'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Nom" required data-testid="drink-name-input" />
            <Input value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} placeholder="Prix (ex: 3.50)" required data-testid="drink-price-input" />
            <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Description (optionnel)" data-testid="drink-desc-input" />

            {/* Image Upload */}
            <div className="space-y-2" data-testid="drink-image-section">
              <label className="text-xs font-semibold text-gray-600 block">Image de la boisson</label>
              {imagePreview ? (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-lg border" />
                  <button type="button" onClick={() => { setImageFile(null); setImagePreview(''); }}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600" data-testid="remove-image-btn">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-400 mb-2">Aucune image</p>
                </div>
              )}
              <label className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors text-sm text-gray-600">
                <ImagePlus size={16} />
                <span>{imagePreview ? 'Changer l\'image' : 'Ajouter une image'}</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} data-testid="drink-image-input" />
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialog(false)} className="flex-1">Annuler</Button>
              <Button type="submit" disabled={uploading} className="flex-1 bg-[#2c1810] hover:bg-[#4a2c1a]" data-testid="drink-submit-btn">
                {uploading ? 'Envoi...' : (editing ? 'Modifier' : 'Ajouter')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Nouvelle categorie</DialogTitle></DialogHeader>
          <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Nom de la categorie" />
          <Button onClick={handleAddCategory} className="w-full bg-[#2c1810] hover:bg-[#4a2c1a]">Creer</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DrinksManagement;
